import { useEffect, useState } from 'react';
import { Loader2, Mail, Paperclip } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { EmailChipInput } from '@/components/ui/email-chip-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PAYMENT_TERMS,
  PAYMENT_TERMS_LABELS,
  PAYMENT_TERMS_NOTES,
  isBadSendOutcome,
  sendOutcomeLabel,
  type PaymentTerms,
  type SendPreview,
} from '@jobos/shared';
import { describeSendError, useSendInvoice, useSendPreview } from '@/hooks/useInvoiceSends';
import { RecipientPicker } from './RecipientPicker';
import { useAddClientApEmail } from '@/hooks/useClients';

/**
 * The confirm-and-edit step before an invoice leaves the building (FR-4.4).
 *
 * RECIPIENTS ARE ALWAYS PREFILLED FROM RESOLUTION, NEVER FROM THE LAST SEND —
 * on a first send and on a resend alike. Reusing whoever the previous email
 * went to is the alternative FR-4.4 rejects: a one-off "cc the site manager
 * this once" would silently become the standing address for every future
 * invoice, and nothing on screen would say so. `preview.recipients` is what the
 * server will use if this dialog changes nothing — the invoice's override when
 * one is set, otherwise the client's resolved contacts.
 *
 * An active override is therefore ANNOUNCED, with one click to drop back to the
 * resolved set. Without that notice this design earns the same objection it
 * levels at the rejected one: an edit made once, invisibly standing.
 *
 * What a resend does show is the previous attempt's OUTCOME, which is the thing
 * the user is actually deciding against ("sent 2 Jul — bounced: ap@client.com").
 *
 * EXACTLY ONE DIALOG. The resend acknowledgement is a mode inside this one, not
 * a dialog on top of it: two open Radix dialogs make the first aria-hidden, so
 * the layer underneath disappears from assistive tech and from Playwright.
 * `PaymentsPanel.tsx:20-27` documents the same failure from the other side.
 */
export interface SendInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  invoiceNumber: string;
  /** Only for cache invalidation — a send moves the job's billing rollup too. */
  jobId?: string;
  /** Fired after a successful send, for callers with caches the hooks don't know. */
  onSent?: () => void;
}

interface Draft {
  recipients: string[];
  subject: string;
  body: string;
  paymentTerms: PaymentTerms;
}

/**
 * What an invoice with no terms yet opens on. 'Due Upon Receipt' is the status
 * quo it is replacing — every invoice sent before this control existed printed
 * no terms at all, and a customer who was never given a date was, in practice,
 * being asked to pay now. Defaulting to a deadline would silently grant credit
 * on every invoice nobody thought about.
 */
const DEFAULT_TERMS: PaymentTerms = 'due_on_receipt';

const formatSendDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

/** "Bounced: mailbox does not exist" — the detail only when the outcome is bad. */
function outcomeLine(lastSend: NonNullable<SendPreview['lastSend']>): string {
  const label = sendOutcomeLabel(lastSend);
  const detail = isBadSendOutcome(lastSend.state) ? lastSend.outcomeDetail : null;
  return detail ? `${label}: ${detail}` : label;
}

export function SendInvoiceDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  jobId,
  onSent,
}: SendInvoiceDialogProps) {
  // The preview is composed server-side per open; a closed dialog has no
  // business asking the server to draft an email.
  const {
    data: preview,
    isLoading,
    isFetching,
    isError,
  } = useSendPreview(invoiceId, { enabled: open });
  const sendInvoice = useSendInvoice(invoiceId, jobId);
  const addApEmail = useAddClientApEmail(preview?.clientId);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [acknowledgedResend, setAcknowledgedResend] = useState(false);
  /**
   * Set synchronously on click, rather than leaning on the mutation's own
   * pending flag alone. Renders are serialised process-wide with a 120-second
   * budget, so a send queued behind a 50-photo inspection report can wait two
   * minutes before its render even starts. A control that looks inert for two
   * minutes gets clicked again, which deepens the very queue it is waiting on.
   * The server refuses the duplicate, but the retry storm should end here.
   */
  const [submitting, setSubmitting] = useState(false);

  // Each open starts from what the server drafted. Guarded on `draft === null`
  // so a background refetch cannot overwrite what the user has typed.
  //
  // That guard is also why the draft must not be seeded from a CACHED preview
  // while this open's refetch is still in flight: whatever seeds it first wins
  // for the rest of the open, so seeding from cache and refetching behind it
  // would pin the dialog to the stale draft and quietly discard the fresh one.
  // Until the fetch lands, `draft` stays null and the dialog shows its
  // preparing state — the same thing it already shows on a first open.
  useEffect(() => {
    if (!open) {
      setDraft(null);
      setAcknowledgedResend(false);
      return;
    }
    if (isFetching) return;
    setDraft((current) =>
      current ?? (preview
        ? {
            recipients: preview.recipients,
            subject: preview.subject,
            body: preview.body,
            // A resend repeats the terms this invoice already carries, so the
            // second copy of a document cannot contradict the first.
            paymentTerms: preview.paymentTerms ?? DEFAULT_TERMS,
          }
        : null),
    );
  }, [open, preview, isFetching]);

  const busy = submitting || sendInvoice.isPending;

  const close = () => {
    if (busy) return;
    onOpenChange(false);
  };

  const submit = async () => {
    if (!draft || busy) return;
    setSubmitting(true);
    try {
      // Recipients are sent explicitly, never omitted. The route compares them
      // against resolution and only writes an override when they differ, so
      // "unchanged" and "deliberately re-affirmed" reach the server as the same
      // thing they are — while omitting them would ask for resolution and
      // quietly discard an override the user just chose to keep.
      await sendInvoice.mutateAsync({
        recipients: draft.recipients,
        subject: draft.subject.trim(),
        body: draft.body.trim(),
        paymentTerms: draft.paymentTerms,
      });
      onSent?.();
      onOpenChange(false);
    } catch {
      // 409 / 422 / 502 each mean something different; the hook toasts the
      // reason and it is repeated inline below, where the modal cannot hide it.
    } finally {
      setSubmitting(false);
    }
  };

  const contactOptions = preview?.contactOptions ?? [];
  /**
   * Recipients that are not on file for this client — the ones that would have
   * to be typed again on the next invoice. Compared case-insensitively, as
   * every other address comparison in the send path is.
   */
  const onFile = new Set(contactOptions.map((option) => option.email.trim().toLowerCase()));
  const unsavedRecipients = (draft?.recipients ?? []).filter(
    (email) => !onFile.has(email.trim().toLowerCase()),
  );

  const failure = sendInvoice.error ? describeSendError(sendInvoice.error) : null;
  const showResendAck = !!preview?.lastSend && !acknowledgedResend;
  const canSend =
    !!draft &&
    draft.recipients.length > 0 &&
    draft.subject.trim().length > 0 &&
    draft.body.trim().length > 0 &&
    !busy;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-lg">
        {isLoading || !draft ? (
          <>
            <DialogHeader>
              <DialogTitle>Send {invoiceNumber}</DialogTitle>
              <DialogDescription>Preparing the email…</DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-center py-8">
              {isError ? (
                <p className="text-sm text-red-600">
                  This invoice&apos;s email could not be prepared. Close and try again.
                </p>
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              )}
            </div>
          </>
        ) : showResendAck && preview?.lastSend ? (
          /* A mode, not a second dialog. */
          <>
            <DialogHeader>
              <DialogTitle>Send {invoiceNumber} again?</DialogTitle>
              <DialogDescription>
                This invoice has already been emailed. Sending again delivers the current PDF.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
              <p className="text-gray-700">
                Sent {formatSendDate(preview.lastSend.createdAt)} to{' '}
                {preview.lastSend.recipients.join(', ')}
              </p>
              <p
                className={
                  isBadSendOutcome(preview.lastSend.state)
                    ? 'mt-1 text-red-600'
                    : 'mt-1 text-gray-500'
                }
              >
                {outcomeLine(preview.lastSend)}
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="button" onClick={() => setAcknowledgedResend(true)}>
                <Mail className="mr-2 h-4 w-4" />
                Send again
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Send {invoiceNumber}</DialogTitle>
              <DialogDescription>
                Check the recipients and the message before this goes to the client.
              </DialogDescription>
            </DialogHeader>

            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div>
                <label
                  htmlFor="send-recipients"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  To
                </label>
                {/* The picker ADDS to the chip input rather than replacing it:
                    typed addresses and multiple recipients both survive. */}
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <EmailChipInput
                      id="send-recipients"
                      value={draft.recipients}
                      onChange={(recipients) => setDraft({ ...draft, recipients })}
                      disabled={busy}
                      suggestions={contactOptions}
                    />
                  </div>
                  <RecipientPicker
                    options={contactOptions}
                    selected={draft.recipients}
                    onChange={(recipients) => setDraft({ ...draft, recipients })}
                    disabled={busy}
                  />
                </div>

                {unsavedRecipients.length > 0 && preview && (
                  /* The address is already going out on THIS invoice either
                     way; this only decides whether the next one has to be typed
                     again. Offered per address rather than as a single "save
                     all" so a genuine one-off cc is not swept onto the client's
                     permanent list alongside the address that belongs there. */
                  <div className="mt-2 space-y-1">
                    {unsavedRecipients.map((email) => (
                      <button
                        key={email}
                        type="button"
                        disabled={busy || addApEmail.isPending}
                        onClick={() => addApEmail.mutate(email)}
                        className="text-sm text-blue-700 underline underline-offset-2 disabled:opacity-50"
                      >
                        {`Add ${email} to ${preview.clientName || 'this client'}'s billing contacts`}
                      </button>
                    ))}
                  </div>
                )}

                {preview?.overrideActive && (
                  <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
                    <p>
                      This invoice has its own billing contacts, kept from a previous edit. They
                      will be used again, and any change here replaces them.
                    </p>
                    {preview.resolvedRecipients.length > 0 && (
                      <button
                        type="button"
                        className="mt-1 font-medium underline underline-offset-2"
                        onClick={() =>
                          setDraft({ ...draft, recipients: preview.resolvedRecipients })
                        }
                      >
                        {"Use the client's contacts instead"} (
                        {preview.resolvedRecipients.join(', ')})
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="send-subject"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Subject
                </label>
                <Input
                  id="send-subject"
                  value={draft.subject}
                  disabled={busy}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="send-body" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <Textarea
                  id="send-body"
                  rows={7}
                  value={draft.body}
                  disabled={busy}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
              </div>

              <div>
                <label
                  htmlFor="send-payment-terms"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Payment terms
                </label>
                <Select
                  value={draft.paymentTerms}
                  disabled={busy}
                  onValueChange={(value) =>
                    setDraft({ ...draft, paymentTerms: value as PaymentTerms })
                  }
                >
                  <SelectTrigger id="send-payment-terms">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((term) => (
                      <SelectItem key={term} value={term}>
                        {PAYMENT_TERMS_LABELS[term]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* The exact sentence the customer will read, shown here because
                    this dropdown's three labels don't say that two of them carry
                    a late-fee warning. */}
                <p className="mt-1 text-xs text-gray-500">
                  Printed on the invoice: {PAYMENT_TERMS_NOTES[draft.paymentTerms]}
                </p>
              </div>

              <p className="flex items-center gap-2 text-sm text-gray-500">
                <Paperclip className="h-4 w-4" />
                <span>{preview?.filename}</span>
              </p>

              {failure && (
                <div
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                  <p className="font-medium">{failure.title}</p>
                  <p>{failure.description}</p>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={close} disabled={busy}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSend}>
                  {busy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Send invoice
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
